import { SocialPlatformIcon } from '@app/modules/social-export/components/SocialPlatformIcon';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ShareIcon from '@mui/icons-material/Share';
import { Box, Button, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { publishMessages } from '../publish-messages';

export interface PublishPlatformMenuProps {
  /** Platforms the user can open the upload wizard for. */
  platforms: SocialPlatform[];
  /** Called with the platform id when the user picks an entry. */
  onSelectPlatform: (platformId: string) => void;
  /** When true, trigger and menu items stay disabled. */
  disabled?: boolean;
  /**
   * When true, only a share icon opens the menu
   * (used next to the “published on …” label on edit cards).
   */
  iconOnly?: boolean;
}

/**
 * Compact "Publicar en" dropdown for choosing a platform (edit cards, overflow actions).
 */
export function PublishPlatformMenu({
  platforms,
  onSelectPlatform,
  disabled = false,
  iconOnly = false,
}: PublishPlatformMenuProps) {
  const { $t } = useIntl();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePlatformSelect = (platformId: string) => {
    onSelectPlatform(platformId);
    handleClose();
  };

  if (platforms.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled">
        <FormattedMessage {...publishMessages.noPlatformsToPublish} />
      </Typography>
    );
  }

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {iconOnly ? (
        <IconButton
          size="small"
          color="primary"
          disabled={disabled}
          onClick={handleMenuClick}
          title={$t(publishMessages.publishTo)}
          sx={{ px: 0.5 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ShareIcon fontSize="small" />
            <ArrowDropDownIcon fontSize="small" sx={{ ml: -0.25, mr: -0.25 }} />
          </Box>
        </IconButton>
      ) : (
        <Button
          size="small"
          disabled={disabled}
          onClick={handleMenuClick}
          startIcon={<ShareIcon />}
          endIcon={<ArrowDropDownIcon />}
        >
          {$t(publishMessages.publishTo)}
        </Button>
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {platforms.map((platform) => (
          <MenuItem
            key={platform.id}
            disabled={disabled}
            onClick={() => handlePlatformSelect(platform.id)}
          >
            <SocialPlatformIcon platform={platform} size="md" sx={{ mr: 1 }} />
            {platform.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
